<script>
import { ValidationProvider } from 'vee-validate'
import { baseComputed } from '@state/helpers/base.js'
import floatLabel from '@src/mixin/floatLabel.js'

export default {
    components: {
        ValidationProvider,
    },

    mixins: [floatLabel],
    props: {
        placeholder: {
            type: [String, Number],
            default: '',
        },
        id: {
            type: String,
            default: '',
        },
        value: {
            type: String,
            default: '',
        },
        className: {
            type: String,
            default: '',
        },
        type: {
            type: String,
            default: 'text',
        },
        name: {
            type: String,
            default: '',
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        rules: {
            type: String,
            default: '',
        },
        required: {
            type: Boolean,
            default: false,
        },
        bigHeight: {
            type: Boolean,
            default: false,
        },
        mask: {
            type: [String, Array, RegExp],
            default: '',
        },
        charCounter: {
            type: Boolean,
            default: false,
        },
        hasClassToggleLabel: {
            type: Boolean,
            default: false,
        },
        hasCharacterCounter: {
            type: Boolean,
            default: false,
        },
        characterLimit: {
            type: Number,
            default: 300,
        },
        login: {
            type: Boolean,
            default: false,
        },
        mode: {
            type: String,
            default: 'aggressive',
        },
    },
    computed: {
        ...baseComputed,
        alertStyle() {
            if (this.type === 'textarea') {
                return { top: '86px' }
            }
            return ''
        },
    },
    methods: {
        focusInput($event) {
            this.$emit('focus', $event)
            this.addClass($event.currentTarget)
        },
        blurInput($event) {
            this.$emit('blur', $event)
            this.toggleClass($event.currentTarget)
        },
        inputChange($event) {
            const inputLabel = document.getElementById(`${this.id}-label`)

            /*
             TODO: localizar onde fica o evento de mudança de input para impedir a label de input sumir quando
                   ele estiver vazio e assim tirar a "solução" abaixo
            */
            // verifica se o input ficou vazio e recoloca a classe de label
            if ($event.length <= 0) {
                setTimeout(() => {
                    inputLabel.classList.add('toggle-label')
                }, 10)
            }

            this.$emit('input', $event)
        },
        checkIsInputMasked() {
            return !this.bigHeight && this.mask !== ''
        },
    },
}
</script>

<template>
    <ValidationProvider
        v-slot="{ errors }"
        :rules="rules"
        :name="name"
        :mode="mode"
    >
        <b-form-group class="mgb_input">
            <b-input-group
                :class="
                    ({
                        'has-append-icon': $slots.hasOwnProperty('append-icon'),
                    },
                    className)
                "
            >
                <label
                    :id="`${id}-label`"
                    :for="id"
                    class="col-form-label pt-0 label-class"
                    :class="{ 'toggle-label': value.length > 0 }"
                >
                    {{ name }}{{ required ? '*' : '' }}
                </label>

                <!-- se for um input de uma linha e tiver mascara -->
                <template v-if="type !== 'textarea'">
                    <b-form-input
                        v-if="!bigHeight && mask !== ''"
                        :id="id"
                        v-mask="mask"
                        :class="{ 'input-error': errors.length }"
                        :disabled="disabled"
                        :type="type"
                        :name="name"
                        :value="value"
                        @input="inputChange($event)"
                        @focus.native="focusInput($event)"
                        @blur.native="blurInput($event)"
                    >
                        <b-input-group-text
                            v-if="$slots.hasOwnProperty('append-icon')"
                        >
                            <slot name="append-icon"></slot>
                        </b-input-group-text>
                    </b-form-input>
                </template>

                <!-- TODO: procurar uma solução mais elegante com esse componente
                           de máscara pois ele não permite valor false -->
                <!-- se for um input de uma linha e NAO tiver mascara -->
                <template v-if="type !== 'textarea'">
                    <b-form-input
                        v-if="!bigHeight && mask === ''"
                        :id="id"
                        :class="{ 'input-error': errors.length }"
                        :disabled="disabled"
                        :type="type"
                        :name="name"
                        :value="value"
                        @input="inputChange($event)"
                        @focus.native="focusInput($event)"
                        @blur.native="blurInput($event)"
                    >
                        <b-input-group-text
                            v-if="$slots.hasOwnProperty('append-icon')"
                        >
                            <slot name="append-icon"></slot>
                        </b-input-group-text>
                    </b-form-input>
                </template>

                <!-- se for input de varias linhas -->
                <template v-if="bigHeight || type === 'textarea'">
                    <b-form-textarea
                        v-if="bigHeight || type === 'textarea'"
                        :id="id"
                        :class="[
                            { inputTextArea: hasCharacterCounter },
                            { 'input-error': errors.length },
                        ]"
                        :disabled="disabled"
                        :type="type"
                        :name="name"
                        :value="value"
                        rows="3"
                        max-rows="6"
                        :maxlength="characterLimit"
                        @input="inputChange($event)"
                        @focus.native="focusInput($event)"
                        @blur.native="blurInput($event)"
                    >
                        <b-input-group-text
                            v-if="$slots.hasOwnProperty('append-icon')"
                        >
                            <slot name="append-icon"></slot>
                        </b-input-group-text>
                    </b-form-textarea>
                </template>

                <!-- contador de caracter -->
                <div
                    v-if="hasCharacterCounter"
                    class="col-form-label char_counter"
                >
                    {{ $t('Inputs.availableCharacters') }}:
                    {{ characterLimit - value.length }}
                </div>

                <span
                    class="alert-text"
                    :style="alertStyle"
                    :class="{ 'login-input-error': login }"
                >
                    {{ errors[0] }}
                </span>
            </b-input-group>
        </b-form-group>
    </ValidationProvider>
</template>

<style lang="scss">
@import '@design';
@import '@src/design/components/mgb-input';
</style>
